import { loadStripe, type Stripe } from '@stripe/stripe-js';

/**
 * Loads the Stripe SDK with retry logic, as per the Stripe team's recommendation.
 * > _If you're using stripe-js v2.3.0 or newer you can wrap loading Stripe JS in try-catch logic and retry loading from there._
 */
export async function loadStripeWithRetry(
  publishableKey: string,
  options: LoadStripeOptions = {},
  attempt = 0,
): Promise<Stripe | null> {
  const { maxRetries = 3, retryDelayMs = 500 } = options;

  try {
    return await loadStripe(publishableKey);
  } catch (error) {
    if (attempt >= maxRetries) {
      throw error;
    }

    await sleep(retryDelayMs * (attempt + 1));

    return loadStripeWithRetry(publishableKey, options, attempt + 1);
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// ─── Types ───────────────────────────────────────────────────────────────────

type LoadStripeOptions = {
  maxRetries?: number;
  retryDelayMs?: number;
};
